/* eslint-disable complexity */
/* eslint-disable max-len */
/**
 * @file 订阅管理弹窗
 */

import React, {type FC, useEffect, useState} from 'react';
import {Modal, App, Button, Tooltip} from 'antd';
import cx from 'classnames';

import BillingSparkIcon from '@/assets/billing/star.svg';
import {getDuMatePurchaseUrl} from '@/utils';

import type {
    ISubscriptionCycleOptionView, ISubscriptionPlanFeatureView, ISubscriptionPlanView
} from '@/stores/BillingStore';
import type {BillingCycleType, BillingEditionType} from '../../../api/billing';
import styles from './index.module.less';

interface ISubscriptionModalProps {
    open: boolean;
    loading: boolean;
    submitting: boolean;
    plans: ISubscriptionPlanView[];
    enterprisePlans: ISubscriptionPlanView[];
    cycleOptions: ISubscriptionCycleOptionView[];
    onCancel: () => void;
    onEnterpriseContact: () => void;
    onLoadPlans: (edition: BillingEditionType, cycle: BillingCycleType) => Promise<void>;
    onLoadEnterprisePlans: () => Promise<void>;
    onSubscribe: (plan: ISubscriptionPlanView, cycle: BillingCycleType) => Promise<void>;
    onCancelAutoRenew?: (params: {
        serviceId: string;
        serviceType: string;
        region: string;
    }) => Promise<void>;
}

interface IModelTagProps {
    text: string;
}

const ModelTag: FC<IModelTagProps> = ({text}) => (
    <span className={styles.modelTag}>{text}</span>
);

const SubscriptionModal: FC<ISubscriptionModalProps> = props => {
    const [edition, setEdition] = useState<BillingEditionType>('personal');
    const [cycle, setCycle] = useState<BillingCycleType>('monthly');
    const [pendingPlanId, setPendingPlanId] = useState<string>('');

    const cycleOptions = props.cycleOptions;
    const {modal, message} = App.useApp();

    useEffect(
        () => {
            if (!props.open) {
                setEdition('personal');
                setCycle('monthly');
                setPendingPlanId('');
                return;
            }

            if (edition !== 'personal') {
                return;
            }

            props.onLoadPlans(edition, cycle).catch(() => undefined);
        },
        [cycle, edition, props.open, props.onLoadPlans]
    );

    useEffect(
        () => {
            if (edition !== 'personal') {
                return;
            }

            const hasCurrentCycle = cycleOptions.some(option => option.id === cycle);
            if (!hasCurrentCycle && cycleOptions[0]) {
                setCycle(cycleOptions[0].id);
            }
        },
        [cycle, cycleOptions, edition]
    );

    // 企业版套餐走接口(edition=enterprise)、复用个人版卡片 UI，弹窗打开时拉取一次
    useEffect(
        () => {
            if (!props.open) {
                return;
            }

            props.onLoadEnterprisePlans().catch(() => undefined);
        },
        [props.open, props.onLoadEnterprisePlans]
    );

    const handleSubscribe = async (plan: ISubscriptionPlanView, cycle: BillingCycleType) => {
        // 企业版「立即订阅」: 新开 DuMate 订阅席位页，不走本地下单
        if (plan.planName === 'plan-enterprise') {
            window.open(getDuMatePurchaseUrl('renew'), '_blank', 'noopener,noreferrer');
            return;
        }
        setPendingPlanId(plan.id);
        try {
            await props.onSubscribe(plan, cycle);
        }
        finally {
            setPendingPlanId('');
        }
    };

    const handleCancelAutoRenew = async (plan: ISubscriptionPlanView) => {
        const {serviceId, serviceType, region} = plan;
        if (!serviceId || !serviceType || !region) {
            message.error('缺少取消连续包月所需参数');
            return;
        }
        setPendingPlanId(serviceId);
        try {
            await props.onCancelAutoRenew?.({
                serviceId,
                serviceType,
                region
            });
            props.onLoadPlans(edition, cycle).catch(() => undefined);
        }
        finally {
            setPendingPlanId('');
        }
    };

    const showCancelConfirm = (plan: ISubscriptionPlanView) => {
        modal.confirm({
            icon: null,
            title: '确认取消连续包月?',
            content: (
                <div>
                    取消不会影响当前权益，进阶版 将于 {plan.subscriptionExpireAt} 到期后停止续费。
                </div>
            ),
            okText: '保留订阅',
            cancelText: '取消连续包月',
            centered: true,
            onOk: () => {},
            onCancel: () => handleCancelAutoRenew(plan)
        });
    };

    const renderSubscribeButton = (plan: ISubscriptionPlanView) => {
        const button = (
            <Button
                block
                type={plan.planName === 'plan-pro'
                    ? undefined
                    : (plan.buttonDisabled ? 'default' : 'primary')}
                className={cx(
                    styles.subscribeBtn,
                    {
                        [styles.proSubscribeBtn]: plan.planName === 'plan-pro',
                        [styles.disabledProSubscribeBtn]:
                            plan.planName === 'plan-pro' && plan.buttonDisabled,
                        [styles.disabledSubscribeBtn]:
                            plan.planName !== 'plan-pro' && plan.buttonDisabled
                    }
                )}
                disabled={plan.buttonDisabled || props.loading}
                loading={props.submitting && pendingPlanId === plan.id}
                onClick={() => handleSubscribe(plan, cycle)}
            >
                {plan.buttonText}
            </Button>
        );

        if (!plan.active || !plan.subscriptionExpireAt) {
            return button;
        }

        return (
            <Tooltip
                title={`你当前订阅的套餐将于 ${plan.subscriptionExpireAt} 到期，新套餐将于到期后生效`}
                placement="top"
            >
                <span className={styles.subscribeBtnTipWrapper}>{button}</span>
            </Tooltip>
        );
    };

    // feature 尾部「立即咨询」等链接：跳转伐谋官网产品页
    const openConsult = () => {
        window.open('https://cloud.baidu.com/product/famou.html', '_blank', 'noopener,noreferrer');
    };

    // 渲染 feature 文案：带 link 时把尾部(最后一个「，」之后)渲染为可点击链接
    const renderFeatureText = (feature: ISubscriptionPlanFeatureView): React.ReactNode => {
        if (!feature.link) {
            return feature.text;
        }
        const sepIndex = feature.text.lastIndexOf('，');
        const prefix = sepIndex >= 0 ? feature.text.slice(0, sepIndex + 1) : '';
        const linkText = sepIndex >= 0 ? feature.text.slice(sepIndex + 1) : feature.text;
        return (
            <>
                {prefix}
                <span
                    role="button"
                    tabIndex={0}
                    className={styles.featureLink}
                    onClick={openConsult}
                >
                    {linkText}
                </span>
            </>
        );
    };

    // 套餐卡片：个人版与企业版复用同一 UI（企业版走 edition=enterprise 接口）
    const renderPlanCard = (plan: ISubscriptionPlanView) => (
        <section key={plan.id} className={styles.planCard}>
            {plan.active && <span className={styles.activeTag}>{plan.activeTagText}</span>}

            <div className={styles.planTitle}>{plan.name}</div>
            <div className={styles.priceRow}>
                <span className={styles.priceMain}>
                    <span className={styles.priceCurrency}>¥</span>
                    <span className={styles.priceAmount}>
                        {plan.priceText.replace('¥', '').trim()}
                    </span>
                </span>
                <span className={styles.priceUnit}>{plan.unitText}</span>
                {plan.oldPriceText && (
                    <span className={styles.priceMeta}>
                        <span className={styles.oldPrice}>{plan.oldPriceText}</span>
                        {plan.promoTagText && <ModelTag text={plan.promoTagText} />}
                    </span>
                )}
            </div>
            {plan.showCancelButton ? (
                <div className={styles.proTip}>
                    <span className={styles.planDesc}>{plan.subtitle}</span>
                    <span
                        className={styles.proTipCancel}
                        onClick={() => showCancelConfirm(plan)}
                        style={{cursor: 'pointer'}}
                    >
                        {plan.cancelButtonText || '取消订阅'}
                    </span>
                </div>
            ) : (
                <p className={styles.planDesc}>{plan.subtitle}</p>
            )}
            {renderSubscribeButton(plan)}

            {plan.highlights.length > 0 && (
                <div className={styles.featureHighlight}>
                    {plan.highlights[0] && (
                        <div className={styles.highlightTitle}>
                            <BillingSparkIcon />
                            <span>{plan.highlights[0].text}</span>
                        </div>
                    )}

                    {plan.highlights.slice(1).map(highlight => (
                        <div key={highlight.key} className={styles.highlightLine}>
                            <span>{highlight.text}</span>
                            {highlight.tag && <span className={styles.tag}>{highlight.tag}</span>}
                        </div>
                    ))}
                </div>
            )}

            <ul className={styles.featureList}>
                {plan.features.map((feature, index) => (
                    <li key={`${plan.id}-feature-${index}`} className={styles.featureItem}>
                        {renderFeatureText(feature)}
                    </li>
                ))}
            </ul>
        </section>
    );

    return (
        <Modal
            centered
            keyboard={false} // 防止按ESC键关闭buyCountLabel
            width={800}
            open={props.open}
            footer={null}
            className={styles.subscriptionModal}
            styles={{content: {height: 'auto'}}}
            title={
                <div className={styles.modalTitleRow}>
                    <span className={styles.modalTitleText}>订阅<span className={styles.planTag}>伐谋套餐</span>，享受更多能力</span>
                </div>
            }
            onCancel={props.onCancel}
            destroyOnHidden
        >
            <div className={styles.modalBody}>
                <div className={styles.topBar}>
                    <div className={styles.editionTabs}>
                        <button
                            type="button"
                            className={cx(styles.editionTab, {[styles.activeEditionTab]: edition === 'personal'})}
                            onClick={() => setEdition('personal')}
                        >
                            个人版
                        </button>
                        <button
                            type="button"
                            className={cx(styles.editionTab, {[styles.activeEditionTab]: edition === 'enterprise'})}
                            onClick={() => setEdition('enterprise')}
                        >
                            企业版
                        </button>
                    </div>

                    {edition === 'personal' && (
                        <div className={styles.cycleTabs}>
                            {cycleOptions.map(option => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={cx(styles.cycleTab, {[styles.activeCycleTab]: cycle === option.id})}
                                    onClick={() => setCycle(option.id)}
                                >
                                    {option.discount ? (
                                        <span>
                                            {`${option.label} · `}
                                            <span className={styles.discountTag}>{option.discount}</span>
                                        </span>
                                    ) : option.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {edition === 'personal' ? (
                    <div className={styles.planGrid}>
                        {props.plans.map(plan => renderPlanCard(plan))}
                    </div>
                ) : (
                    <section className={styles.enterprisePanel}>
                        {props.enterprisePlans.length === 0 ? (
                            <div className={styles.enterpriseLoading}>加载中...</div>
                        ) : (
                            <div className={styles.enterpriseCardWrapper}>
                                {props.enterprisePlans.map(plan => renderPlanCard(plan))}
                            </div>
                        )}
                    </section>
                )}
            </div>
        </Modal>
    );
};

export default SubscriptionModal;
